// Has this account finished onboarding?
//
// Two sources disagree by design:
//
//   localStorage  instant, and wrong on a new device or after clearing data.
//   the database  authoritative, and a round trip away.
//
// Both are consulted, in that order, because the cost of each kind of error is
// not the same. Believing someone finished when they have not lets an account
// through with no photo. Believing someone has NOT finished when they have
// throws a member back into a flow they already completed — much louder, and
// much more annoying.
//
// So: localStorage saying "complete" is accepted immediately, and the database
// is what any redirect actually waits for.

import { useEffect, useState } from 'react';
import { onboardingStorage } from '../../services/Onboarding';
import { profileService } from '../../services/Profile/profileService';
import { useAuthContext } from '../../providers';
import { logger } from '../../utils/logger';

export type OnboardingStatus = 'unknown' | 'complete' | 'incomplete';

export function useOnboardingStatus(): OnboardingStatus {
  const { user, loading } = useAuthContext();
  const [status, setStatus] = useState<OnboardingStatus>(() =>
    onboardingStorage.isOnboardingComplete() ? 'complete' : 'unknown'
  );

  useEffect(() => {
    // Nobody signed in, or we do not know yet. Either way there is no account
    // to hold to anything.
    if (loading || !user?.id) return;
    // Already settled locally; the database cannot make this more complete.
    if (status === 'complete') return;

    let cancelled = false;

    profileService
      .getOnboardingCompleted()
      .then((completed) => {
        if (cancelled) return;
        setStatus(completed ? 'complete' : 'incomplete');
      })
      .catch((error) => {
        // A failed check must not lock anyone out of the app. Staying
        // 'unknown' lets navigation through, which is the safe direction to
        // fail in: the worst case is one account browsing without a photo,
        // against the alternative of every account trapped by an outage.
        if (cancelled) return;
        logger.log('[ONBOARDING] Could not confirm completion:', error);
        setStatus('unknown');
      });

    return () => { cancelled = true; };
  }, [user?.id, loading, status]);

  return status;
}
